import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ROLES, PERMISSIONS, DEFAULT_PERMS } from "@/lib/labels";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", branch_id: "", permissions: [] });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const openDialog = (u) => {
    setEditing(u || null);
    setForm(u ? { name: u.name, email: u.email, password: "", role: u.role, branch_id: u.branch_id || "", permissions: u.permissions || [...(DEFAULT_PERMS[u.role] || [])] } : { name: "", email: "", password: "", role: "staff", branch_id: "", permissions: [...(DEFAULT_PERMS.staff || [])] });
    setOpen(true);
  };

  const togglePerm = (k, v) =>
    setForm((f) => ({ ...f, permissions: v ? [...f.permissions, k] : f.permissions.filter((p) => p !== k) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, branch_id: form.branch_id || null, permissions: form.permissions.length ? form.permissions : null };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Pengguna diperbarui");
      } else {
        await api.post("/users", { ...form, permissions: form.permissions.length ? form.permissions : null });
        toast.success("Pengguna ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus pengguna ini?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Pengguna" subtitle="Kelola akun admin & staf">
        <Button data-testid="add-user-button" onClick={() => openDialog(null)} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
          <Plus className="w-4 h-4 mr-1" /> Tambah Pengguna
        </Button>
      </PageHeader>

      {!users ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : users.length === 0 ? (
        <EmptyState icon={UsersIcon} title="Belum ada pengguna" />
      ) : (
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
          <table className="w-full text-sm min-w-[560px]" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#1A1A22] text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Nama</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Email</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Role</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cabang</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                  <td className="px-4 py-3 font-medium text-zinc-100">{u.name} {u.id === me?.id && <span className="text-xs text-zinc-500">(Anda)</span>}</td>
                  <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider border ${u.role === "admin" ? "text-blue-500 bg-blue-500/10 border-blue-500/20" : u.role === "admin_cabang" ? "text-sky-400 bg-sky-500/10 border-sky-500/20" : "text-zinc-400 bg-zinc-500/10 border-zinc-500/20"}`}>
                      {ROLES[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{u.branch_name || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`edit-user-${u.id}`} onClick={() => openDialog(u)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                      {u.id !== me?.id && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="p-1.5 text-zinc-500 hover:text-rose-500 transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <div className="space-y-2">
              <Label className={labelCls}>Nama</Label>
              <Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Email</Label>
              <Input data-testid="user-email-input" type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={`${inputCls} disabled:opacity-50`} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>{editing ? "Kata Sandi Baru (opsional)" : "Kata Sandi"}</Label>
              <Input data-testid="user-password-input" type="password" required={!editing} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v, permissions: [...(DEFAULT_PERMS[v] || [])] }))}>
                <SelectTrigger data-testid="user-role-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  <SelectItem value="admin">Owner</SelectItem>
                  <SelectItem value="admin_cabang">Admin Cabang</SelectItem>
                  <SelectItem value="admin_ho">Admin HO</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "admin_cabang" && (
              <div className="space-y-2">
                <Label className={labelCls}>Cabang</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}>
                  <SelectTrigger data-testid="user-branch-select" className={inputCls}><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.role !== "admin" && (
              <div className="space-y-2">
                <Label className={labelCls}>Kewenangan</Label>
                <p className="text-[11px] text-zinc-500">Kewenangan bawaan role sudah tercentang otomatis — sesuaikan bila perlu.</p>
                <div className="space-y-2 p-3 border border-[#1A1A22] rounded-md">
                  {Object.entries(PERMISSIONS).filter(([k]) => !(form.role === "admin_cabang" && k === "input_biaya_operasional")).map(([k, l]) => (
                    <div key={k} className="flex items-center gap-2">
                      <Checkbox id={`perm-${k}`} data-testid={`perm-${k}`} checked={form.permissions.includes(k)} onCheckedChange={(v) => togglePerm(k, !!v)} />
                      <Label htmlFor={`perm-${k}`} className="text-sm text-zinc-300 font-normal">{l}</Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="user-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
